import { ReactNode } from "react";

/**
 * One inline SVG sprite for the icons that repeat on a page.
 *
 * A product grid draws 24 hearts twice (white backdrop plus outline), a
 * rating draws five stars, the header and footer each draw the logo. Inlined,
 * every one of those carries its full path data: on the live category page the
 * two heart icons alone were 26 KB of the server HTML, and inline SVG was
 * 65 KB on a product page. Here each shape is emitted once as a `<symbol>`,
 * and the icon components below point at it with `<use href="#id">`, which is
 * ~60 bytes per instance instead of ~500.
 *
 * `<IconSprite />` renders once per document in `pages/_app.js`, so any icon on
 * any page, in any portal or modal, can reference it. `<use>` resolves against
 * the whole document, so the sprite's position in the body does not matter.
 * Colour still works: `fill="currentColor"` inside a symbol inherits the
 * `color` of the `<svg>` that references it, exactly as the inline path did.
 *
 * Only put an icon here when it repeats or is large. A one-off icon costs the
 * same either way, and every symbol here ships on every page.
 *
 * Symbol ids share the document's id space with everything else, so they are
 * prefixed. Any `<clipPath>`/`<defs>` id inside a symbol must be unique in this
 * file: the sprite is one `<svg>`, and two symbols both defining `prefix__a`
 * (which every Figma export does) would clash.
 */
export type SpriteSymbol = {
  id: string;
  viewBox: string;
  content: ReactNode;
};

export const SPRITE_SYMBOLS = {
  /** `@/assets/icons/figma/IconStateHeartFilled` */
  heartFilled: {
    id: "wi-heart-filled",
    viewBox: "0 0 24 24",
    content: (
      <path
        fill="currentColor"
        d="M13.35 20.456c-.76.703-1.93.703-2.69-.01l-.11-.103c-5.248-4.845-8.676-8.017-8.546-11.975.06-1.734.93-3.396 2.339-4.375C6.982 2.157 10.24 3.013 12 5.115c1.759-2.102 5.018-2.969 7.657-1.122 1.41.979 2.279 2.641 2.339 4.375.14 3.958-3.299 7.13-8.547 11.996z"
      />
    ),
  },
  /** `@/assets/icons/figma/IconStateHeartOutline` */
  heartOutline: {
    id: "wi-heart-outline",
    viewBox: "0 0 24 24",
    content: (
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M12 5.115c1.76-2.102 5.018-2.958 7.657-1.122 1.4.979 2.28 2.631 2.34 4.365.13 3.958-3.3 7.13-8.547 11.985l-.11.102c-.76.714-1.93.714-2.69.01l-.1-.091-.06-.056c-5.214-4.834-8.625-7.997-8.486-11.94.06-1.744.94-3.396 2.34-4.375C6.981 2.146 10.24 3.013 12 5.115m.001 13.831.1-.102c4.758-4.396 7.897-7.303 7.897-10.251 0-2.04-1.5-3.57-3.499-3.57-1.54 0-3.038 1.01-3.558 2.407h-1.87c-.53-1.397-2.029-2.407-3.568-2.407-2 0-3.499 1.53-3.499 3.57 0 2.948 3.139 5.855 7.897 10.251z"
        clipRule="evenodd"
      />
    ),
  },
  /** `@/assets/icons/24/Star` */
  star: {
    id: "wi-star",
    viewBox: "0 0 24 24",
    content: (
      <path
        d="M11.9975 17.7483L17.0117 20.9245C17.3802 21.1586 17.8361 20.8125 17.7391 20.3748L16.4104 14.3889L20.8329 10.3678C21.153 10.0726 20.9881 9.51268 20.5517 9.47196L14.7228 8.95278L12.4437 3.31304C12.2788 2.89565 11.7163 2.89565 11.5514 3.31304L9.27222 8.95278L3.44338 9.47196C3.01664 9.51268 2.84206 10.0726 3.17182 10.3678L7.59437 14.3889L6.26566 20.3748C6.16868 20.8125 6.62451 21.1586 6.99305 20.9245L11.9975 17.7483Z"
        fill="currentColor"
      />
    ),
  },
  /** `@/assets/icons/24/EmptyStar` */
  emptyStar: {
    id: "wi-star-empty",
    viewBox: "0 0 24 24",
    content: (
      <path
        d="M12.3989 17.1148L11.9971 16.8603L11.5956 17.1151L7.1281 19.9505L8.32655 14.5514L8.42083 14.1267L8.09891 13.834L4.06832 10.1693L9.33876 9.69982L9.7957 9.65912L9.96758 9.23379L11.9975 4.21076L14.0275 9.23379L14.1993 9.65912L14.6563 9.69982L19.9356 10.1701L15.9058 13.834L15.5839 14.1267L15.6782 14.5514L16.8768 19.9513L12.3989 17.1148ZM6.59087 20.2915C6.59102 20.2914 6.59116 20.2913 6.59131 20.2912L6.59116 20.2913L6.59087 20.2915Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    ),
  },
  /** `@/assets/icons/24/HalfStar` */
  halfStar: {
    id: "wi-star-half",
    viewBox: "0 0 24 24",
    content: (
      <>
        <path
          d="M12.3989 17.1148L11.9971 16.8603L11.5956 17.1151L7.1281 19.9505L8.32655 14.5514L8.42083 14.1267L8.09891 13.834L4.06832 10.1693L9.33876 9.69982L9.7957 9.65912L9.96758 9.23379L11.9975 4.21076L14.0275 9.23379L14.1993 9.65912L14.6563 9.69982L19.9356 10.1701L15.9058 13.834L15.5839 14.1267L15.6782 14.5514L16.8768 19.9513L12.3989 17.1148ZM6.59087 20.2915C6.59105 20.2913 6.59123 20.2912 6.59141 20.2911L6.59116 20.2913L6.59087 20.2915Z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <g clipPath="url(#wi-star-half-clip)">
          <path
            d="M12.0001 3V17.7498H11.9982L6.9932 20.9245C6.62477 21.1589 6.16896 20.8133 6.26628 20.3754L7.59498 14.3893L3.17194 10.3684C2.84225 10.0725 3.01703 9.51241 3.44404 9.4717L9.27224 8.95333L11.5523 3.31281C11.6347 3.10328 11.8174 3 12.0001 3Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="wi-star-half-clip">
            <rect
              width="9"
              height="18"
              fill="white"
              transform="translate(3 3)"
            />
          </clipPath>
        </defs>
      </>
    ),
  },
  /** `@/assets/icons/24/Search` */
  search: {
    id: "wi-search",
    viewBox: "0 0 24 24",
    content: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.3645 14.608H16.1986L20.6753 19.1032C21.1082 19.5359 21.1082 20.2429 20.6753 20.6755C20.2424 21.1082 19.535 21.1082 19.1021 20.6755L14.6148 16.1909V15.3572L14.3298 15.0618C12.8516 16.328 10.835 16.9823 8.69161 16.6235C5.75639 16.1275 3.41244 13.6794 3.05345 10.7248C2.50442 6.26128 6.26319 2.50471 10.7294 3.05342C13.6857 3.41219 16.1352 5.75477 16.6315 8.68827C16.9905 10.8304 16.3359 12.8458 15.0688 14.3231L15.3645 14.608ZM5.11232 9.85955C5.11232 12.487 7.23455 14.608 9.86358 14.608C12.4926 14.608 14.6148 12.487 14.6148 9.85955C14.6148 7.23207 12.4926 5.11108 9.86358 5.11108C7.23455 5.11108 5.11232 7.23207 5.11232 9.85955Z"
        fill="currentColor"
      />
    ),
  },
  /** `@/assets/icons/24/ChevronDown` */
  chevronDown: {
    id: "wi-chevron-down",
    viewBox: "0 0 24 24",
    content: (
      <path
        d="M17.6877 9.31093C17.2731 8.89636 16.6034 8.89636 16.1889 9.31093L12.0643 13.4355L7.93981 9.31093C7.52523 8.89636 6.85553 8.89636 6.44095 9.31093C6.02637 9.72551 6.02637 10.3952 6.44095 10.8098L11.3202 15.6891C11.7348 16.1036 12.4045 16.1036 12.8191 15.6891L17.6984 10.8098C18.1023 10.4058 18.1023 9.72551 17.6877 9.31093Z"
        fill="currentColor"
      />
    ),
  },
  /** `@/assets/icons/24/ChevronRight` */
  chevronRight: {
    id: "wi-chevron-right",
    viewBox: "0 0 24 24",
    content: (
      <path
        d="M9.31093 6.31228C8.89636 6.72685 8.89636 7.39656 9.31093 7.81114L13.4355 11.9357L9.31093 16.0602C8.89636 16.4748 8.89636 17.1445 9.31093 17.559C9.72551 17.9736 10.3952 17.9736 10.8098 17.559L15.6891 12.6798C16.1036 12.2652 16.1036 11.5955 15.6891 11.1809L10.8098 6.30165C10.4058 5.8977 9.72551 5.8977 9.31093 6.31228Z"
        fill="currentColor"
      />
    ),
  },
  /** `WhoppahFullSize` in `@/assets/icons/Logo` (header and footer wordmark) */
  whoppahFullSize: {
    id: "wi-whoppah-logo",
    viewBox: "0 0 152 40",
    content: (
      <>
        <path
          d="M140.827 17.2498C138.619 17.2498 136.838 18.0358 135.497 19.4029V11.177H129.993V34.9195H135.494V26.957C135.494 23.6403 136.903 22.1708 139.028 22.1708C140.817 22.1708 142.02 23.5378 142.02 25.5548V34.9224H147.487V24.3926C147.498 20.0865 144.815 17.2498 140.827 17.2498Z"
          fill="currentColor"
        />
        <path
          d="M149.72 11.4287C148.466 11.4287 147.44 12.3863 147.44 13.7201C147.44 15.0426 148.466 16.0002 149.72 16.0002C150.974 16.0002 152 15.0426 152 13.7201C152 12.3863 150.974 11.4287 149.72 11.4287ZM149.72 15.5556C148.728 15.5556 147.907 14.769 147.907 13.7201C147.907 12.6485 148.728 11.8619 149.72 11.8619C150.723 11.8619 151.544 12.6485 151.544 13.7201C151.544 14.769 150.723 15.5556 149.72 15.5556ZM150.712 13.2983C150.712 12.8765 150.37 12.6029 149.857 12.6029H148.888V14.7006H149.344V14.028H149.686L150.278 14.7006H150.826L150.176 13.9824C150.506 13.8798 150.712 13.6289 150.712 13.2983ZM149.344 13.6175V13.0133H149.857C150.096 13.0133 150.256 13.1273 150.256 13.3097C150.256 13.4921 150.096 13.6175 149.857 13.6175H149.344Z"
          fill="currentColor"
        />
        <path
          d="M99.252 17.3865C97.0168 17.3865 95.2292 18.07 93.8878 19.2658V17.8988H88.3867V40.0004H93.8878V33.689C95.2292 34.8863 97.0168 35.5699 99.252 35.5699C103.825 35.5699 107.676 31.9106 107.676 26.4774C107.676 21.0779 103.825 17.3865 99.252 17.3865ZM98.1152 30.9212C95.88 30.9212 93.8848 29.1443 93.8848 26.4774C93.8848 23.8428 95.88 22.0351 98.1152 22.0351C100.521 22.0351 102.275 23.8457 102.275 26.4774C102.278 29.1443 100.524 30.9212 98.1152 30.9212Z"
          fill="currentColor"
        />
        <path
          d="M18.637 27.0626L15.3696 17.8004H11.4852L8.21783 27.029L5.39806 17.8004H0L5.53647 34.9929H10.3161L13.4451 25.6955L16.5387 34.9929H21.3184L26.8548 17.8004H21.4568L18.637 27.0626Z"
          fill="currentColor"
        />
        <path
          d="M39.1899 17.2498C36.9901 17.2498 35.201 18.0358 33.8611 19.4029V11.177H28.3585V34.9195H33.8611V26.957C33.8611 23.6403 35.2702 22.1708 37.395 22.1708C39.1826 22.1708 40.387 23.5378 40.387 25.5548V34.9224H45.8543V24.3926C45.8617 20.0865 43.1788 17.2498 39.1899 17.2498Z"
          fill="currentColor"
        />
        <path
          d="M56.7539 17.3865C51.5266 17.3865 47.1946 21.0106 47.1946 26.4101C47.1946 31.8448 51.5266 35.4674 56.7539 35.4674C61.8766 35.4674 66.2101 31.8448 66.2101 26.4101C66.2101 21.0106 61.8766 17.3865 56.7539 17.3865ZM56.7539 30.8524C54.5187 30.8524 52.4837 29.1091 52.4837 26.4086C52.4837 23.7433 54.5128 21.9664 56.7539 21.9664C58.886 21.9664 60.9151 23.7433 60.9151 26.4086C60.9151 29.1121 58.886 30.8539 56.7539 30.8539V30.8524Z"
          fill="currentColor"
        />
        <path
          d="M78.5525 17.3865C76.3173 17.3865 74.5297 18.07 73.1883 19.2658V17.8988H67.6872V40.0004H73.1883V33.689C74.5297 34.8863 76.3173 35.5699 78.5525 35.5699C83.126 35.5699 86.9765 31.9106 86.9765 26.4774C86.9765 21.0779 83.126 17.3865 78.5525 17.3865ZM77.4217 30.9212C75.1865 30.9212 73.1927 29.1443 73.1927 26.4774C73.1927 23.8428 75.1865 22.0351 77.4217 22.0351C79.8291 22.0351 81.5829 23.8457 81.5829 26.4774C81.5784 29.1443 79.8247 30.9212 77.4217 30.9212Z"
          fill="currentColor"
        />
        <path
          d="M122.874 19.2658C121.533 18.07 119.745 17.3865 117.51 17.3865C112.937 17.3865 109.086 21.0779 109.086 26.4774C109.086 31.9121 112.937 35.5699 117.51 35.5699C119.745 35.5699 121.533 34.8863 122.874 33.689V35.0561H128.376V17.8988H122.874V19.2658ZM118.646 30.9212C116.238 30.9212 114.484 29.1443 114.484 26.4774C114.484 23.8428 116.238 22.0351 118.646 22.0351C120.879 22.0351 122.874 23.8457 122.874 26.4774C122.874 29.1443 120.879 30.9212 118.646 30.9212Z"
          fill="currentColor"
        />
        <path
          d="M77.1567 10.5693C77.4187 10.5699 77.6711 10.4712 77.8625 10.2934C78.054 10.1156 78.1701 9.87195 78.1873 9.61205L78.7056 1.78571C78.7184 1.7073 78.7253 1.62804 78.7262 1.5486C78.7262 1.53542 78.7262 1.52371 78.7262 1.51054V1.48127C78.7088 1.08259 78.5372 0.705996 78.2472 0.430023C77.9573 0.154049 77.5713 0 77.1699 0C76.7685 0 76.3826 0.154049 76.0926 0.430023C75.8027 0.705996 75.6311 1.08259 75.6136 1.48127H75.5945L76.126 9.61205C76.1432 9.87195 76.2594 10.1156 76.4508 10.2934C76.6423 10.4712 76.8947 10.5699 77.1567 10.5693Z"
          fill="currentColor"
        />
        <path
          d="M82.5936 11.7828L88.5273 6.61306C88.5927 6.56949 88.6542 6.52053 88.7113 6.46669L88.7364 6.43888L88.7584 6.41985C89.0343 6.12673 89.1848 5.73849 89.1781 5.33711C89.1714 4.93574 89.0081 4.55267 88.7225 4.26881C88.437 3.98496 88.0517 3.82255 87.6479 3.81589C87.2441 3.80922 86.8536 3.95883 86.5587 4.2331L86.5455 4.21993L81.1404 10.3367C80.9693 10.5324 80.8792 10.7852 80.8883 11.0444C80.8973 11.3035 81.0048 11.5495 81.1891 11.733C81.3735 11.9164 81.6209 12.0235 81.8815 12.0328C82.1422 12.042 82.3966 11.9527 82.5936 11.7828Z"
          fill="currentColor"
        />
        <path
          d="M73.0529 11.7358C73.2376 11.5519 73.3451 11.305 73.3536 11.0451C73.3621 10.7853 73.2709 10.532 73.0985 10.3365L67.6979 4.20955L67.6832 4.22273C67.3883 3.94845 66.9978 3.79885 66.594 3.80551C66.1902 3.81217 65.8049 3.97459 65.5194 4.25844C65.2338 4.54229 65.0705 4.92536 65.0638 5.32673C65.057 5.72811 65.2075 6.11636 65.4834 6.40947L65.5055 6.4285C65.5144 6.43728 65.5217 6.44753 65.532 6.45631C65.5889 6.50979 65.65 6.55873 65.7146 6.60268L71.6483 11.7724C71.8436 11.9441 72.0972 12.0357 72.3578 12.029C72.6185 12.0222 72.8668 11.9174 73.0529 11.7358Z"
          fill="currentColor"
        />
      </>
    ),
  },
  /** `@/assets/icons/figma/IconNavigationChevronDown` */
  navChevronDown: {
    id: "wi-nav-chevron-down",
    viewBox: "0 0 24 24",
    content: (
      <>
        <path
          fill="currentColor"
          d="M17.688 9.293a1.06 1.06 0 0 0-1.499 0l-4.124 4.125L7.94 9.293a1.059 1.059 0 1 0-1.499 1.499l4.88 4.88a1.06 1.06 0 0 0 1.498 0l4.88-4.88a1.07 1.07 0 0 0-.011-1.499"
        />
      </>
    ),
  },
  /** `@/assets/icons/figma/IconNavigationChevronRight` */
  navChevronRight: {
    id: "wi-nav-chevron-right",
    viewBox: "0 0 24 24",
    content: (
      <>
        <path
          fill="currentColor"
          d="M8.31 6.294a1.06 1.06 0 0 0 0 1.5l4.125 4.124-4.124 4.124a1.059 1.059 0 1 0 1.499 1.499l4.88-4.879a1.06 1.06 0 0 0 0-1.499l-4.88-4.88a1.07 1.07 0 0 0-1.5.011"
        />
      </>
    ),
  },
  /** `@/assets/icons/figma/IconStateVerifiedOutlined` */
  verifiedOutlined: {
    id: "wi-verified-outlined",
    viewBox: "0 0 24 24",
    content: (
      <>
        <path
          fill="currentColor"
          d="m23 12.482-2.4-2.8.3-3.7-3.6-.8-1.9-3.2-3.4 1.5-3.4-1.5-1.9 3.2-3.6.8.3 3.7-2.4 2.8 2.4 2.8-.3 3.7 3.6.8 1.9 3.2 3.4-1.5 3.4 1.5 1.9-3.2 3.6-.8-.3-3.7zm-4.3 4.9-2.7.6-1.4 2.4-2.6-1.1-2.6 1.1-1.4-2.4-2.7-.6.2-2.8-1.8-2.1 1.8-2.1-.2-2.8 2.7-.6 1.4-2.4 2.6 1.1 2.6-1.1 1.4 2.4 2.7.6-.2 2.8 1.8 2.1-1.8 2.1zm-2.1-9.3 1.4 1.4-8 8-4-4 1.4-1.4 2.6 2.6z"
        />
      </>
    ),
  },
  /** `@/assets/icons/figma/IconStateZap` */
  zap: {
    id: "wi-zap",
    viewBox: "0 0 24 24",
    content: (
      <>
        <path
          fill="currentColor"
          d="M20.766 9.508h-8.265l2.004-5.855c.375-1.246-1.128-2.243-2.13-1.246L2.358 12.374c-.751.747-.25 2.118.877 2.118h8.265l-2.004 5.855c-.375 1.246 1.128 2.243 2.13 1.246l10.018-9.967c.751-.747.25-2.118-.877-2.118"
        />
      </>
    ),
  },
  /** `@/assets/icons/figma/IconThemeTagLabelFilled` */
  tagLabelFilled: {
    id: "wi-tag-label-filled",
    viewBox: "0 0 24 24",
    content: (
      <>
        <path
          fill="currentColor"
          fillRule="evenodd"
          d="m12.369 3.504 8.1 8.1c.324.324.531.783.531 1.278s-.198.945-.531 1.27l-6.3 6.3a1.76 1.76 0 0 1-1.269.53c-.495 0-.945-.198-1.269-.522l-8.1-8.1A1.8 1.8 0 0 1 3 11.082v-6.3c0-.99.81-1.8 1.8-1.8h6.3c.495 0 .945.198 1.269.522M5 6.482c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5-1.5.67-1.5 1.5"
          clipRule="evenodd"
        />
      </>
    ),
  },
  /** `@/assets/icons/24/Spark` */
  spark: {
    id: "wi-spark",
    viewBox: "0 0 24 24",
    content: (
      <>
        <path
          d="M11.9963 14.8C12.2605 14.8004 12.5149 14.7183 12.708 14.5702C12.9011 14.4222 13.0182 14.2194 13.0355 14.003L13.5582 7.48678C13.571 7.4215 13.578 7.3555 13.5789 7.28936C13.5789 7.27839 13.5789 7.26864 13.5789 7.25767V7.2333C13.5613 6.90136 13.3883 6.58781 13.0959 6.35804C12.8035 6.12826 12.4144 6 12.0096 6C11.6048 6 11.2157 6.12826 10.9233 6.35804C10.6309 6.58781 10.4579 6.90136 10.4404 7.2333H10.421L10.957 14.003C10.9744 14.2194 11.0914 14.4222 11.2845 14.5702C11.4776 14.7183 11.732 14.8004 11.9963 14.8Z"
          fill="currentColor"
        />
        <path
          d="M16.1479 16.7652L21.4214 11.9211C21.4795 11.8803 21.5342 11.8344 21.5849 11.784L21.6072 11.7579L21.6268 11.7401C21.872 11.4654 22.0057 11.1017 21.9998 10.7256C21.9938 10.3495 21.8487 9.99055 21.5949 9.72458C21.3411 9.45861 20.9986 9.30643 20.6398 9.30019C20.281 9.29395 19.9338 9.43413 19.6718 9.69112L19.66 9.67878L14.8563 15.4102C14.7043 15.5935 14.6241 15.8305 14.6321 16.0732C14.6402 16.316 14.7357 16.5466 14.8996 16.7185C15.0634 16.8903 15.2833 16.9907 15.5149 16.9994C15.7466 17.008 15.9727 16.9244 16.1479 16.7652Z"
          fill="currentColor"
        />
        <path
          d="M9.10068 16.7252C9.26487 16.5529 9.36042 16.3218 9.36795 16.0785C9.37548 15.8353 9.29444 15.5981 9.14124 15.4151L4.34132 9.67849L4.32823 9.69082C4.06618 9.43402 3.71907 9.29395 3.36022 9.30019C3.00138 9.30642 2.6589 9.45849 2.40513 9.72426C2.15135 9.99002 2.00615 10.3487 2.00019 10.7245C1.99423 11.1003 2.12799 11.4638 2.3732 11.7382L2.39283 11.7561C2.40068 11.7643 2.40722 11.7739 2.41638 11.7821C2.46696 11.8322 2.52121 11.878 2.57865 11.9191L7.85227 16.7595C8.0259 16.9202 8.25125 17.0061 8.48291 16.9997C8.71457 16.9933 8.93532 16.8953 9.10068 16.7252Z"
          fill="currentColor"
        />
      </>
    ),
  },
  /** `@/assets/icons/24/line/Heart` */
  lineHeart: {
    id: "wi-line-heart",
    viewBox: "0 0 24 24",
    content: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.0003 5.13249C13.7596 3.03127 17.0183 2.17446 19.6573 4.01048C21.0568 4.98968 21.9365 6.6421 21.9964 8.37611C22.1264 12.3337 18.6977 15.506 13.4497 20.3612L13.3398 20.4632C12.5801 21.1772 11.4105 21.1772 10.6508 20.4734L10.5508 20.3816L10.4905 20.3256C5.27649 15.4918 1.86471 12.3287 2.00412 8.38631C2.06409 6.6421 2.94376 4.98968 4.34322 4.01048C6.9822 2.16426 10.241 3.03127 12.0003 5.13249ZM12.001 18.9638L12.101 18.8618C16.8592 14.4656 19.998 11.5585 19.998 8.61072C19.998 6.5707 18.4985 5.04069 16.4993 5.04069C14.9599 5.04069 13.4605 6.05049 12.9407 7.44791H11.0714C10.5416 6.05049 9.04218 5.04069 7.50277 5.04069C5.50354 5.04069 4.00412 6.5707 4.00412 8.61072C4.00412 11.5585 7.14291 14.4656 11.9011 18.8618L12.001 18.9638Z"
          fill="currentColor"
        />
      </>
    ),
  },
  /** `@/assets/icons/24/MedalOutline` */
  medalOutline: {
    id: "wi-medal-outline",
    viewBox: "0 0 24 25",
    content: (
      <>
        <g clipPath="url(#wi-medal-outline-clip)">
          <path
            d="M14.94 19.525L12 17.795L9.06001 19.525L9.84001 16.185L7.25001 13.945L10.66 13.655L12 10.525L13.34 13.655L16.75 13.945L14.16 16.185M20 2.02502H4.00001V4.02502L8.86001 7.66502C7.16442 8.38861 5.77077 9.6756 4.91475 11.3083C4.05873 12.9411 3.79287 14.8194 4.16215 16.6255C4.53143 18.4317 5.51317 20.0549 6.94135 21.2206C8.36952 22.3863 10.1565 23.023 12 23.023C13.8435 23.023 15.6305 22.3863 17.0587 21.2206C18.4869 20.0549 19.4686 18.4317 19.8379 16.6255C20.2071 14.8194 19.9413 12.9411 19.0853 11.3083C18.2293 9.6756 16.8356 8.38861 15.14 7.66502L20 4.02502M18 15.025C18.0005 16.1613 17.6784 17.2744 17.0711 18.2347C16.4638 19.1951 15.5962 19.9632 14.5694 20.4498C13.5426 20.9364 12.3987 21.1215 11.2709 20.9834C10.143 20.8453 9.07757 20.3898 8.19847 19.6699C7.31937 18.9499 6.66277 17.9952 6.30507 16.9167C5.94737 15.8382 5.90327 14.6803 6.1779 13.5777C6.45253 12.4751 7.03461 11.4731 7.85641 10.6884C8.6782 9.90372 9.70593 9.36849 10.82 9.14502C11.5985 8.98498 12.4015 8.98498 13.18 9.14502C14.5389 9.4176 15.7616 10.1522 16.6402 11.2241C17.5189 12.296 17.9994 13.639 18 15.025ZM12.63 7.02502H11.37L7.37001 4.02502H16.71L12.63 7.02502Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="wi-medal-outline-clip">
            <rect
              width="24"
              height="24"
              fill="white"
              transform="translate(0 0.0250244)"
            />
          </clipPath>
        </defs>
      </>
    ),
  },
  /** `@/assets/icons/24/Cart` */
  cart: {
    id: "wi-cart",
    viewBox: "0 0 24 24",
    content: (
      <>
        <path
          d="M19 20C19 21.11 18.11 22 17 22C15.89 22 15 21.1 15 20C15 18.89 15.89 18 17 18C18.11 18 19 18.9 19 20ZM7 18C5.89 18 5 18.89 5 20C5 21.1 5.89 22 7 22C8.11 22 9 21.11 9 20C9 18.89 8.11 18 7 18ZM7.2 14.63L7.17 14.75C7.17 14.89 7.28 15 7.42 15H19V17H7C5.89 17 5 16.1 5 15C5 14.65 5.09 14.32 5.24 14.04L6.6 11.59L3 4H1V2H4.27L5.21 4H20C20.55 4 21 4.45 21 5C21 5.17 20.95 5.34 20.88 5.5L17.3 11.97C16.96 12.58 16.3 13 15.55 13H8.1L7.2 14.63ZM8.5 11H10V9H7.56L8.5 11ZM11 9V11H14V9H11ZM14 8V6H11V8H14ZM17.11 9H15V11H16L17.11 9ZM18.78 6H15V8H17.67L18.78 6ZM6.14 6L7.08 8H10V6H6.14Z"
          fill="currentColor"
        />
      </>
    ),
  },
  /** `@/assets/icons/24/Shipping` */
  shipping: {
    id: "wi-shipping",
    viewBox: "0 0 24 24",
    content: (
      <>
        <path
          d="M21 16.5C21 16.88 20.79 17.21 20.47 17.38L12.57 21.82C12.41 21.94 12.21 22 12 22C11.79 22 11.59 21.94 11.43 21.82L3.53 17.38C3.21 17.21 3 16.88 3 16.5V7.5C3 7.12 3.21 6.79 3.53 6.62L11.43 2.18C11.59 2.06 11.79 2 12 2C12.21 2 12.41 2.06 12.57 2.18L20.47 6.62C20.79 6.79 21 7.12 21 7.5V16.5ZM12 4.15L10.11 5.22L16 8.61L17.96 7.5L12 4.15ZM6.04 7.5L12 10.85L13.96 9.75L8.08 6.35L6.04 7.5ZM5 15.91L11 19.29V12.58L5 9.21V15.91ZM19 15.91V9.21L13 12.58V19.29L19 15.91Z"
          fill="currentColor"
        />
      </>
    ),
  },
} satisfies Record<string, SpriteSymbol>;

/**
 * The sprite itself. Rendered once, in `pages/_app.js`. Zero-sized rather than
 * `display: none`: a hidden SVG is not rendered at all in some engines, and
 * then nothing that references it renders either.
 */
export const IconSprite = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className="absolute h-0 w-0 overflow-hidden"
      data-icon-sprite=""
    >
      {Object.values(SPRITE_SYMBOLS).map(symbol => (
        <symbol key={symbol.id} id={symbol.id} viewBox={symbol.viewBox}>
          {symbol.content}
        </symbol>
      ))}
    </svg>
  );
};
